/**
 * ServicesCard — 4th column on the Landing pricing grid.
 *
 * Renders the menu of "Additional Services" (concierge upsells) plus a
 * lightweight inquiry modal that POSTs to /api/services/inquire. Inquiries
 * notify Tracy via email so he can follow up personally — high-margin
 * upsells live or die by speed-to-conversation.
 */
import { useEffect, useState } from "react";
import { X, Sparkles, Phone, Users, UserCheck, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_BASE =
  (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL) || "";

const SERVICES = [
  {
    key: "concierge_onboarding",
    icon: Sparkles,
    name: "Concierge Onboarding",
    price: "$499 one-time",
    pitch: "90-min Zoom · CSV import · pipeline setup · first 10 leads pre-scored with you live.",
  },
  {
    key: "vanity_phone",
    icon: Phone,
    name: "Vanity Phone Number",
    price: "$15/mo",
    pitch: "Premium memorable digits in your area code — builds local trust on caller ID.",
  },
  {
    key: "dfy_buyer_list",
    icon: Users,
    name: "Done-For-You Buyer List",
    price: "$199 / market",
    pitch: "200+ vetted cash buyers with phone + email, hand-delivered in 48 hours.",
  },
  {
    key: "va_placement",
    icon: UserCheck,
    name: "VA Placement",
    price: "$499 + $499/mo",
    pitch: "We source, interview, train & manage a wholesaling VA for your team.",
  },
];


export default function ServicesCard() {
  const [picked, setPicked] = useState(null);  // service key when modal open
  return (
    <>
      <div
        className="price-card relative"
        data-testid="pricing-card-services"
        style={{ background: "linear-gradient(160deg, #FFFBEB 0%, #FFF7E0 60%, #FEF3C7 100%)" }}
      >
        <span
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-[0.18em] bg-amber-500 text-white shadow-sm z-10"
        >
          Money makers
        </span>
        <div className="font-display font-bold text-2xl mb-1">Additional Services</div>
        <div className="text-[var(--ink-2)] text-sm mb-5">
          Premium add-ons &amp; done-for-you services.
        </div>

        <ul className="space-y-3.5 mb-7">
          {SERVICES.map((s) => {
            const Icon = s.icon;
            return (
              <li
                key={s.key}
                className="rounded-lg border border-amber-200/70 bg-white/80 p-3 backdrop-blur-sm"
                data-testid={`pricing-service-${s.key}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Icon size={15} className="text-amber-700 shrink-0" />
                    <span className="font-bold text-[13px] text-[var(--ink)] leading-tight">{s.name}</span>
                  </div>
                  <span className="text-[10px] font-bold text-amber-800 whitespace-nowrap shrink-0">
                    {s.price}
                  </span>
                </div>
                <div className="text-xs text-[var(--ink-2)] leading-snug mb-2">{s.pitch}</div>
                <button
                  type="button"
                  onClick={() => setPicked(s)}
                  className="text-xs font-bold text-amber-800 hover:text-amber-900 inline-flex items-center gap-1"
                  data-testid={`pricing-service-${s.key}-cta`}
                >
                  Talk to us <ArrowRight size={11} />
                </button>
              </li>
            );
          })}
        </ul>

        <div className="text-[11px] text-[var(--ink-3)] italic text-center mt-auto">
          Custom requirements? Reply to any of these and Our Team will tailor a quote.
        </div>
      </div>

      {picked && (
        <InquiryModal
          service={picked}
          onClose={() => setPicked(null)}
        />
      )}
    </>
  );
}


function InquiryModal({ service, onClose }) {
  const [name, setName]     = useState("");
  const [email, setEmail]   = useState("");
  const [phone, setPhone]   = useState("");
  const [notes, setNotes]   = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone]     = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API_BASE}/api/services/inquire`, {
        service: service.key,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        notes: notes.trim(),
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit — please try again.");
    } finally {
      setSending(false);
    }
  };

  const Icon = service.icon;
  return (
    <div
      className="fixed inset-0 z-[60] bg-zinc-900/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
      onClick={onClose}
      data-testid="services-inquiry-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl border border-[var(--line)] w-full max-w-md p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-[var(--ink-3)] hover:text-[var(--ink)] rounded-full p-1.5 hover:bg-slate-100"
          aria-label="Close"
          data-testid="services-inquiry-close"
        >
          <X size={16} />
        </button>

        {!done ? (
          <>
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                <Icon size={18} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">
                  {service.price}
                </div>
                <div className="font-display font-bold text-lg text-[var(--ink)] leading-tight">
                  {service.name}
                </div>
              </div>
            </div>
            <p className="text-sm text-[var(--ink-2)] mb-4 mt-2">{service.pitch}</p>

            <form onSubmit={submit} className="space-y-3" data-testid="services-inquiry-form">
              <input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--line)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                required
                data-testid="services-inquiry-name"
              />
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--line)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                required
                data-testid="services-inquiry-email"
              />
              <input
                type="tel"
                placeholder="Phone (optional)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--line)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                data-testid="services-inquiry-phone"
              />
              <textarea
                placeholder="Anything specific I should know? (your market, team size, urgency…)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-[var(--line)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                data-testid="services-inquiry-notes"
              />
              <button
                type="submit"
                disabled={sending}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold text-sm py-2.5 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                data-testid="services-inquiry-submit"
              >
                {sending ? "Sending…" : <>Talk to Our Team <ArrowRight size={14} /></>}
              </button>
              <p className="text-[10px] text-[var(--ink-3)] text-center">
                Our Team personally replies within 2 hours during business hours.
              </p>
            </form>
          </>
        ) : (
          <div className="text-center py-4" data-testid="services-inquiry-done">
            <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-3">
              <Check size={22} />
            </div>
            <div className="font-display font-bold text-lg text-[var(--ink)]">
              Got it — Our Team will be in touch.
            </div>
            <p className="text-sm text-[var(--ink-2)] mt-2 max-w-sm mx-auto">
              You&apos;ll get a personal reply within 2 hours (business hours) about
              <strong> {service.name}</strong>.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-5 btn-outline text-sm"
              data-testid="services-inquiry-done-close"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
